import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Collapse } from "react-bootstrap";
import { useAuth } from "../context/AuthContext";
import useTheme from "../utils/useTheme";
import {
  LayoutDashboard,
  Mail,
  FolderKanban,
  Car,
  List,
  Plus,
  ClipboardCheck,
  Users,
  
  Shield,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  User,
  LogOut,
  BarChart3,
  Wallet,
  Clock,
  TrendingUp,
  FileText,
  Sun,
  Moon,
  Gavel,
} from "lucide-react";
import "./sidebar.css";

const EXPANDED = 248;
const COLLAPSED = 72;

export const SIDEBAR_WIDTH = EXPANDED;
export const SIDEBAR_WIDTH_COLLAPSED = COLLAPSED;

function getInitials(nameOrEmail) {
  if (!nameOrEmail) return "U";
  const parts = String(nameOrEmail).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function Sidebar({ onWidthChange, mobileOpen, onCloseMobile }) {
  const { user: auth, logout } = useAuth();
  const user = auth?.user || auth;

  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  const [collapsed, setCollapsed] = useState(() => {
    const v = localStorage.getItem("crm_sidebar_collapsed");
    return v === null ? true : v === "true";
  });

  const [openStock, setOpenStock] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  useEffect(() => {
    const w = collapsed ? COLLAPSED : EXPANDED;
    onWidthChange?.(w);
    localStorage.setItem("crm_sidebar_collapsed", String(collapsed));
  }, [collapsed, onWidthChange]);

  useEffect(() => {
    const inStock =
      location.pathname.startsWith("/dashboard/stock-list") ||
      location.pathname.startsWith("/dashboard/add-car");
    if (inStock) setOpenStock(true);
  }, [location.pathname]);

  const sections = useMemo(() => {
    const main = [
      { to: "/dashboard",        label: "Dashboard",    icon: LayoutDashboard },
      { to: "/dashboard/leads",  label: "Leads",        icon: FolderKanban },
      {
        group: "stock",
        label: "Car Stock",
        icon: Car,
        children: [
          { to: "/dashboard/stock-list", label: "View Stock List", icon: List },
          { to: "/dashboard/add-car",   label: "Add Car",         icon: Plus },
        ],
      },
      { to: "/dashboard/tasks", label: "Tasks & Notes", icon: ClipboardCheck },
      { to: "/dashboard/email", label: "Email Center", icon: Mail },
      { to: "/dashboard/auction", label: "Auction", icon: Gavel },
    ];

    const extraNav = [];
    if (user?.role !== "Admin") {
      extraNav.push({ to: "/dashboard/my-followups", label: "My Followups", icon: ClipboardCheck });
    }
    extraNav.push({ to: "/dashboard/my-customers", label: "My Customers", icon: Users });
    main.splice(3, 0, ...extraNav);

    const people = [];
    if (user?.role === "Manager" || user?.role === "Admin") {
      people.push(
        { to: "/dashboard/my-team",       label: "My Team",        icon: Users },
        { to: "/dashboard/team-portfolio", label: "Team Portfolio", icon: BarChart3 },
        { to: "/dashboard/attendance",    label: "Attendance",     icon: Clock }
      );
    }
    if (user?.role === "Agent") {
      people.push(
        { to: "/dashboard/attendance",    label: "Attendance",     icon: Clock }
      );
    }

    const payroll = [
      { to: "/dashboard/payroll", label: "Payroll",        icon: Wallet },
      { to: "/dashboard/sales",   label: "Sales Tracking", icon: TrendingUp },
    ];

    const admin = [];
    if (user?.role === "Admin") {
      admin.push(
        { to: "/dashboard/control-panel",   label: "Control Panel",      icon: Shield },
        { to: "/dashboard/reports",         label: "Reports",            icon: FileText }
      );
    }

    return [
      { title: "Main",    items: main },
      { title: "People",  items: people },
      { title: "Payroll", items: payroll },
      { title: "Admin",   items: admin },
    ].filter((s) => s.items.length > 0);
  }, [user?.role]);

  const toggleCollapsed = () => {
    setProfileOpen(false);
    setCollapsed((v) => !v);
  };

  const displayName = user?.name || user?.email || "User";
  const role = user?.role || "User";
  const initials = getInitials(displayName);

  return (
    <>
      <div 
        className={`crm-sidebar-overlay ${mobileOpen ? "show" : ""}`} 
        onClick={onCloseMobile} 
      />
      <aside className={`crm-sidebar ${collapsed ? "collapsed" : "expanded"} ${mobileOpen ? "mobile-open" : ""}`}>
        <div className="crm-sidebar__top">
          <div 
            className="crm-brand" 
            onClick={collapsed ? toggleCollapsed : undefined}
            title={collapsed ? "Click to expand sidebar" : "PAJ CRM Executive"}
            style={{ cursor: collapsed ? "pointer" : "default" }}
          >
            <img 
              src="/logo.png" 
              alt="PAJ CRM" 
              className="crm-logo-img" 
              onError={(e) => { e.target.onerror = null; e.target.outerHTML = '<div class="crm-logo">P</div>'; }} 
            />
            {!collapsed && (
              <div className="crm-brand__meta">
                <span className="crm-brand__title">PAJ CRM</span>
                <span className="crm-brand__badge">Executive</span>
              </div>
            )}
          </div>

          <button 
            type="button"
            className="crm-toggle" 
            onClick={toggleCollapsed}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <ChevronRight size={13} strokeWidth={2.5} /> : <ChevronLeft size={16} />}
          </button>
        </div>

        <nav className="crm-nav" data-tour="sidebar-nav">
          {sections.map((section) => (
            <div key={section.title} className="crm-section">
              <div className="crm-section__title">
                {!collapsed ? section.title : <span className="crm-divider" />}
              </div>

              {section.items.map((item) => {
                if (item.group === "stock") {
                  const Icon = item.icon;
                  const inStock =
                    location.pathname.startsWith("/dashboard/stock-list") ||
                    location.pathname.startsWith("/dashboard/add-car");

                  return (
                    <div key="stock-group" className="crm-group">
                      <button
                        type="button"
                        className={`crm-item crm-group__btn ${inStock ? "active" : ""}`}
                        onClick={() => {
                          setProfileOpen(false);
                          if (collapsed) setCollapsed(false);
                          setOpenStock((v) => !v);
                        }}
                        data-tooltip={collapsed ? item.label : undefined}
                        title={collapsed ? item.label : undefined}
                      >
                        <span className="crm-item__icon">
                          <Icon size={18} />
                        </span>

                        {!collapsed && (
                          <>
                            <span className="crm-item__label">{item.label}</span>
                            <span className="crm-item__right">
                              {openStock ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </span>
                          </>
                        )}
                      </button>

                      {!collapsed && (
                        <Collapse in={openStock}>
                          <div className="crm-subnav">
                            {item.children.map((c) => {
                              const CIcon = c.icon;
                              return (
                                <NavLink
                                  key={c.to}
                                  to={c.to}
                                  className={({ isActive }) =>
                                    `crm-subitem ${isActive ? "active" : ""}`
                                  }
                                  onClick={() => {
                                    setProfileOpen(false);
                                    onCloseMobile?.();
                                  }}
                                >
                                  <span className="crm-subitem__icon">
                                    <CIcon size={16} />
                                  </span>
                                  <span className="crm-subitem__label">{c.label}</span>
                                </NavLink>
                              );
                            })}
                          </div>
                        </Collapse>
                      )}
                    </div>
                  );
                }

                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) => `crm-item ${isActive ? "active" : ""}`}
                    data-tooltip={collapsed ? item.label : undefined}
                    title={collapsed ? item.label : undefined}
                    onClick={() => {
                      setProfileOpen(false);
                      onCloseMobile?.();
                    }}
                  >
                    <span className="crm-item__icon">
                      <Icon size={18} />
                    </span>
                    {!collapsed && <span className="crm-item__label">{item.label}</span>}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

      <div className="crm-user">
        <button
          className="crm-user__btn"
          onClick={() => setProfileOpen((v) => !v)}
          data-tooltip={collapsed ? `${displayName} (${role})` : undefined}
          title={collapsed ? `${displayName} (${role})` : undefined}
        >
          {user?.avatar ? (
            <img src={user.avatar} alt="DP" className="crm-user__avatar" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }} />
          ) : (
            <div className="crm-user__avatar">{initials}</div>
          )}

          {!collapsed && (
            <div className="crm-user__meta">
              <div className="crm-user__name">{displayName}</div>
              <div className="crm-user__role">{role}</div>
            </div>
          )}

          {!collapsed && (
            <span className="crm-user__chev">
              {profileOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </span>
          )}
        </button>

        {!collapsed && (
          <Collapse in={profileOpen}>
            <div className="crm-user__menu">
              <NavLink to="/dashboard/profile" className="crm-user__menuitem">
                <User size={16} /> Profile
              </NavLink>

              <button type="button" className="crm-user__menuitem" onClick={toggleTheme}>
                {theme === "dark" ? <><Sun size={15} /> Light Mode</> : <><Moon size={15} /> Dark Mode</>}
              </button>

              <button
                type="button"
                className="crm-user__menuitem danger"
                onClick={() => {
                  setProfileOpen(false);
                  if (typeof logout === "function") logout();
                }}
              >
                <LogOut size={16} /> Logout
              </button>
            </div>
          </Collapse>
        )}
      </div>

      <div className="crm-footer">© {new Date().getFullYear()} ZIWMTECH</div>
    </aside>
    </>
  );
}